import { FormEvent, useState } from 'react'
import { ArrowLeft, Eye, EyeOff, Loader2, Lock, ShieldCheck } from 'lucide-react'
import { Navigate, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { supabase } from '@/lib/supabaseClient'
import { useAuthStore } from '@/stores/authStore'

/**
 * First-time members arrive here right after their magic-link sign-in
 * succeeds. They pick a password so future logins can use the password mode
 * on /login. We persist via supabase.auth.updateUser (the user is already
 * authenticated at this point) and tag user_metadata.passwordSet = true so
 * the auth callback skips this page on subsequent logins.
 */
const SetPasswordPage = () => {
  const navigate = useNavigate()
  const { status, supabaseUser, refreshProfile, signOut } = useAuthStore()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Bootstrapping — wait
  if (status === 'unknown') return null

  // Not authenticated — bounce to login. The page only makes sense for a
  // user who just finished the magic-link callback.
  if (status === 'anonymous' || status === 'denied') {
    return <Navigate to="/login" replace />
  }

  // Already has a password on file — nothing to do.
  if (supabaseUser?.user_metadata?.['passwordSet'] === true) {
    return <Navigate to="/dashboard" replace />
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setSubmitting(true)
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password,
        data: { passwordSet: true },
      })
      if (updateError) throw updateError
      toast.success('Password saved. You can use it next time you sign in.')
      // Re-pull the profile so any downstream consumers see the fresh metadata.
      await refreshProfile()
      navigate('/dashboard', { replace: true })
    } catch (err: any) {
      const message: string = err?.message || ''
      if (/should be at least/i.test(message) || /weak/i.test(message)) {
        setError('Pick a longer or stronger password.')
      } else if (/same as the old/i.test(message)) {
        setError('That password is already on file. Choose a different one.')
      } else {
        setError(message || 'Could not save your password. Try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: 'url(/fonts/images/login_background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-blue-950/85 via-blue-900/75 to-blue-950/95" />
      <div className="relative z-10 w-full max-w-md">
        <button
          type="button"
          onClick={async () => {
            await signOut()
            navigate('/login', { replace: true })
          }}
          className="inline-flex items-center gap-1 text-blue-100 hover:text-white text-sm mb-6"
        >
          <ArrowLeft size={14} /> Sign out
        </button>
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-full bg-white/10 border border-white/20 flex items-center justify-center mb-4">
            <ShieldCheck size={28} className="text-gold" />
          </div>
          <h1 className="serif-font text-3xl font-bold text-white mb-1">Create your password</h1>
          <p className="text-blue-100/80 text-sm">
            Welcome to Shorewood! Set a password to finish setting up your account — you'll use it to
            sign in from now on.
          </p>
        </div>
        <div className="glass-effect bg-white/10 border-white/20 rounded-2xl p-7 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            <label className="block">
              <span className="block text-sm font-medium text-white mb-2">New password</span>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={show ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full pl-10 pr-11 py-3 bg-white/95 border border-gray-200 rounded-lg input-focus text-gray-900 placeholder-gray-400"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={show ? 'Hide password' : 'Show password'}
                >
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-white mb-2">Confirm password</span>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={show ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter your password"
                  className="w-full pl-10 pr-11 py-3 bg-white/95 border border-gray-200 rounded-lg input-focus text-gray-900 placeholder-gray-400"
                />
              </div>
            </label>

            {error && (
              <div className="bg-red-500/15 border border-red-300/40 text-red-100 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !password || !confirm}
              className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full bg-white text-blue-900 font-semibold hover:bg-blue-50 transition disabled:opacity-60 shadow-lg"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save password'
              )}
            </button>

            <p className="text-center text-xs text-blue-100/70">
              You can always change or reset this later from Profile → Security.
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}

export default SetPasswordPage
